/**
 * Hottie — one assistant for the whole ecosystem.
 *
 * There is exactly one Hottie. Not one per product: one. A user who asks
 * Hottie something on .travel and something else on .music is talking to the
 * same assistant with the same memory, and the moment that stops being true
 * the product has thirteen chatbots wearing one name.
 *
 * Hottie opens in the kit sheet from a long-press on the Orb, in every world.
 * Chat and Voice are two modes of that one assistant, not two products. The
 * `/hottie` deep link below stays for hosts that need the URL; the default
 * hold must not navigate there. That was the old "one Hottie lives on .app"
 * link, and it stole the gesture.
 *
 * ── Voice Stack contract ───────────────────────────────────────────────────
 *
 * Voice Stack attaches when the sheet matches:
 *
 *   .hottr-hottie-layer[data-open="true"][data-mode="voice"]
 *
 * Chat and Voice are a mode switch on that layer. There is no second voice
 * button, no second Orb, and no mic dock. Switching the sheet to Voice sets
 * `data-mode="voice"` so Voice Stack can attach. The dock/rail Orb stays the
 * visual.
 *
 * Kit does not run a voice session. Voice Stack drives the existing dock/rail
 * Orb by dispatching:
 *
 *   window.dispatchEvent(new CustomEvent('hottr:hottie:voice', {
 *     detail: { phase: 'listening', level: 0.4 },
 *   }))
 *
 * Detail is `{ phase, level }`. phase names, exactly:
 * idle | listening | thinking | speaking.
 * level: 0..1 RMS (mic in or speech out).
 *
 * Mapping onto the existing HottrOrb — no new OrbState names, no fifth voice
 * state, no second Orb:
 *
 *   idle       → resting     intensity 1
 *   listening  → interested  intensity 0.5 + level
 *   thinking   → connected   intensity 1
 *   speaking   → intense     intensity 0.5 + level
 *
 * `idle` means the Hottie sheet is OPEN and there is no audio (level 0).
 * It is not “sheet closed”. A closed sheet ignores the voice feed and the
 * Orb follows the usual resting / interested / fan (`activated`) rules.
 *
 * `activated` remains fan-open. Speaking is never `activated`.
 *
 * Subscribe while the Hottie sheet is open. Closed sheets do not listen.
 *
 * ── Configuration ──────────────────────────────────────────────────────────
 *
 *   VITE_HOTTIE_URL   defaults to the canonical home below
 *
 * Override it in local development to point at a dev instance. Do not override
 * it per product to give a product its own Hottie — that is the one thing this
 * module exists to prevent.
 */

import type { OrbState } from './HottrOrb';

/** Where the `/hottie` deep link lives. One place, deliberately. */
const CANONICAL_HOTTIE_HOME = 'https://hottr.app';

function hottieHome(): string {
  let configured: string | undefined;
  try {
    configured = import.meta.env?.VITE_HOTTIE_URL as string | undefined;
  } catch {
    // No import.meta.env — a non-Vite host, or a test runner. Fall through to
    // the canonical home rather than throwing: failing to read an optional
    // override is not a reason to break the Orb.
  }
  return (configured || CANONICAL_HOTTIE_HOME).replace(/\/$/, '');
}

/**
 * The URL that opens Hottie as a page, carrying where to come back to.
 *
 * Used by hosts that need the deep link. The Orb's hold does not call this.
 *
 * `returnTo` is sent as an absolute URL because the destination is a different
 * origin and a bare path would be meaningless there. It is built from the
 * caller's own `window.location`, never from anything a page supplies, so this
 * cannot be turned into an open redirect.
 */
export function hottieHref(): string {
  const url = new URL('/hottie', hottieHome());
  if (typeof window !== 'undefined') {
    url.searchParams.set('return_to', window.location.href);
    url.searchParams.set('from', window.location.hostname);
  }
  return url.toString();
}

/**
 * Navigate to the `/hottie` deep link.
 *
 * Explicit. The Orb's default hold opens the in-kit sheet instead — calling
 * this from a hold is the old "one Hottie lives on .app" behaviour and must
 * not return.
 */
export function openHottie(): void {
  if (typeof window === 'undefined') return;
  window.location.assign(hottieHref());
}

/**
 * Selector Voice Stack uses to attach. Locked. Do not rename the class or
 * move `data-open` / `data-mode` off `.hottr-hottie-layer`.
 */
export const HOTTIE_VOICE_ATTACH =
  '.hottr-hottie-layer[data-open="true"][data-mode="voice"]';

/** Voice Stack → kit. Listen only while the Hottie sheet is open. */
export const HOTTIE_VOICE_EVENT = 'hottr:hottie:voice';

/**
 * Kit composer → host. Fired on send (Enter or the Send button) with
 * `{ text }`. Worlds that only mount OrbFan / OrbRail get this with no slot
 * wiring. Kit does not talk to a model.
 */
export const HOTTIE_SEND_EVENT = 'hottr:hottie:send';

export type HottieSendDetail = { text: string };

/**
 * Dispatch `hottr:hottie:send`. Empty / whitespace is a no-op.
 * Default target is `document` so a world can listen without mounting slots.
 */
export function dispatchHottieSend(text: string, target?: EventTarget): void {
  const trimmed = text.trim();
  if (!trimmed) return;
  const eventTarget =
    target ?? (typeof document !== 'undefined' ? document : undefined);
  eventTarget?.dispatchEvent(
    new CustomEvent<HottieSendDetail>(HOTTIE_SEND_EVENT, {
      bubbles: true,
      detail: { text: trimmed },
    }),
  );
}

export const HOTTIE_VOICE_PHASES = ['idle', 'listening', 'thinking', 'speaking'] as const;

export type HottieVoicePhase = (typeof HOTTIE_VOICE_PHASES)[number];

export type HottieVoiceDetail = {
  phase: HottieVoicePhase;
  /** 0..1 RMS. Mic in or speech out. */
  level: number;
};

export type HottieOrbVisual = {
  state: OrbState;
  intensity: number;
};

export const isHottieVoicePhase = (value: unknown): value is HottieVoicePhase =>
  typeof value === 'string' && (HOTTIE_VOICE_PHASES as readonly string[]).includes(value);

/**
 * Map a Voice Stack phase onto the existing HottrOrb.
 *
 * `activated` is reserved for "the fan is open" and is never returned here.
 * Speaking is `intense`. Thinking ignores `level` so the Orb does not wobble
 * on RMS while it thinks.
 */
export function hottieVoiceToOrb(
  phase: HottieVoicePhase,
  level = 0,
): HottieOrbVisual {
  const rms = Math.max(0, Math.min(1, Number.isFinite(level) ? level : 0));
  switch (phase) {
    case 'idle':
      return { state: 'resting', intensity: 1 };
    case 'listening':
      return { state: 'interested', intensity: 0.5 + rms };
    case 'thinking':
      return { state: 'connected', intensity: 1 };
    case 'speaking':
      return { state: 'intense', intensity: 0.5 + rms };
  }
}

export type HottieMode = 'chat' | 'voice';

export type HottieSpeaker = 'hottie' | 'you';

/** One line of the CONVERSATION transcript. Kit never invents a reply. */
export type HottieMessage = {
  id: string;
  speaker: HottieSpeaker;
  text: string;
};

/** Append a You-line for the in-sheet default transcript (no consumer slot). */
export function hottieYouLine(text: string, id?: string): HottieMessage {
  return {
    id: id ?? `you-${Date.now()}`,
    speaker: 'you',
    text: text.trim(),
  };
}
